/**
 * R-562: scrub a failed request's secrets off the error object.
 *
 * An axios error carries the request it came from: `error.config.headers`
 * holds the admin's session token (the `token` header) and `error.config.data`
 * holds the body, which on sign-in is the typed password. Anything that prints
 * the error (about 90 `console.error('...', error)` calls, error reporters,
 * `error.toJSON()`) printed them too. BaseService calls this on every failed
 * response, before the error propagates, so nothing downstream has to remember.
 *
 * Only the error's copy of the request is touched, after the request was sent:
 * what went to the server is unchanged. A retry builds a new request through
 * the request interceptor, which reads the token afresh.
 */

const REDACTED = '[redacted]';

// Substring match on purpose: newPassword, confirmPassword, accessToken, resetToken, x-api-key...
const SECRET_KEY = /token|authorization|password|passwd|secret|cookie|api[-_]?key|^otp$/i;
const isSecretKey = (key: string): boolean => SECRET_KEY.test(key);

const MAX_DEPTH = 8;

function redactDeep(value: any, depth = 0): any {
  if (depth > MAX_DEPTH || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => redactDeep(v, depth + 1));
  const out: Record<string, any> = {};
  for (const key of Object.keys(value)) {
    out[key] = isSecretKey(key) ? REDACTED : redactDeep(value[key], depth + 1);
  }
  return out;
}

function redactHeaders(headers: any, depth = 0): void {
  if (!headers || typeof headers !== 'object' || depth > 2) return;
  for (const key of Object.keys(headers)) {
    if (isSecretKey(key)) headers[key] = REDACTED;
    else if (headers[key] && typeof headers[key] === 'object') redactHeaders(headers[key], depth + 1); // common / post maps
  }
}

function redactString(body: string): string {
  try {
    const parsed = JSON.parse(body);
    if (parsed !== null && typeof parsed === 'object') return JSON.stringify(redactDeep(parsed));
    return body;
  } catch {
    // not JSON: a form-encoded body (a=1&password=x) or plain text
  }
  return body.replace(/(^|&)([^=&]+)=([^&]*)/g, (whole, amp, key, _val) =>
    isSecretKey(decodeKey(key)) ? `${amp}${key}=${REDACTED}` : whole,
  );
}

function decodeKey(key: string): string {
  const spaced = key.replace(/\+/g, ' ');
  try {
    return decodeURIComponent(spaced);
  } catch {
    return spaced; // a malformed % escape: test the raw key rather than skip the body
  }
}

function redactBody(data: any): any {
  if (typeof data === 'string') return redactString(data);
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    const secretKeys: string[] = [];
    data.forEach((_v, k) => {
      if (isSecretKey(k)) secretKeys.push(k);
    });
    secretKeys.forEach((k) => data.set(k, REDACTED));
    return data;
  }
  return redactDeep(data);
}

function redactConfig(config: any): void {
  if (!config || typeof config !== 'object') return;
  redactHeaders(config.headers);
  if (config.data !== undefined && config.data !== null) config.data = redactBody(config.data);
}

export function redactRequestSecrets<E>(error: E): E {
  try {
    const err: any = error;
    const config = err?.config;
    const responseConfig = err?.response?.config;
    redactConfig(config);
    if (responseConfig && responseConfig !== config) redactConfig(responseConfig);
  } catch {
    // Scrubbing must never replace the real error with a different one.
  }
  return error;
}
